import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Card, PageHeader } from '@elysian/ui';
import type { OrganisationSettings } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate } from '../invoices/shared';
import { firstYearRange, periodCycle, FREQUENCIES } from './orgSettings';
import { BUSINESS_KEYS, BusinessFields, formFrom, patchFor, VAT_KEYS, VatFields, YEAR_KEYS, YearFields, type OrgForm } from './OrgFields';

const STEPS = [
  { key: 'business', title: 'Your business', intro: 'How the business appears on invoices and returns.', keys: BUSINESS_KEYS },
  { key: 'vat', title: 'VAT', intro: 'Whether the business charges VAT, and how often returns are filed.', keys: VAT_KEYS },
  { key: 'year', title: 'Financial year', intro: 'When the year starts and the currency the books are kept in. These can’t change once entries are posted.', keys: YEAR_KEYS },
  { key: 'done', title: 'Check and start', intro: 'Everything here can be changed later under Settings → Organisation (the year and currency only until something is posted).', keys: [] },
] as const;

/** Remembered for this browser tab: "Finish later" stops the overview sending people back here. */
export const SETUP_LATER_KEY = 'el-setup-later';

/**
 * First run for new books: the business, VAT and the year, one step at a
 * time. Each step saves as it goes, so a problem shows where it was made.
 */
export function Setup() {
  const { source, load, context } = useSession();
  const navigate = useNavigate();
  const toast = useToast((t) => t.show);
  const data = useAsync(() => source.organisationSettings(), [source]);
  const [s, setS] = useState<OrganisationSettings | null>(null);
  const [f, setF] = useState<OrgForm | null>(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data.state === 'ready' && !s) { setS(data.data); setF(formFrom(data.data)); }
  }, [data, s]);

  const set = (p: Partial<OrgForm>) => setF((cur) => (cur ? { ...cur, ...p } : cur));
  const cur = STEPS[step]!;

  async function next() {
    if (!s || !f) return;
    setError(null);
    setBusy(true);
    try {
      // The VAT step always confirms registration, so "registered, no number" is caught here rather than later.
      const patch = cur.key === 'done' ? { setup_completed: true }
        : cur.key === 'vat' ? { ...patchFor(s, f, cur.keys), vat_registered: f.vat_registered, ...(f.vat_registered ? { vat_number: f.vat_number.trim() || null } : {}) }
        : patchFor(s, f, cur.keys);
      if (Object.keys(patch).length) {
        const saved = await source.updateOrganisation(patch);
        setS(saved);
      }
      if (cur.key === 'done') {
        await load();
        toast('Your books are ready');
        navigate('/', { replace: true });
        return;
      }
      setStep(step + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function later() {
    try { sessionStorage.setItem(SETUP_LATER_KEY, '1'); } catch { /* storage may be blocked */ }
    navigate('/', { replace: true });
  }

  const name = context?.organisation.legal_name ?? s?.legal_name ?? 'your books';
  return (
    <>
      <PageHeader title={`Set up ${name}`} subtitle={`Step ${step + 1} of ${STEPS.length}: ${cur.title}`} />
      {data.state === 'error' && <Banner>Couldn’t load the settings: {data.error.message}</Banner>}
      <ol className="el-wizard-steps" aria-label="Setup steps">
        {STEPS.map((x, i) => (
          <li key={x.key} aria-current={i === step ? 'step' : undefined} data-done={i < step ? 'true' : undefined}>
            <span className="el-wizard-num" aria-hidden="true">{i < step ? '✓' : i + 1}</span>{x.title}
          </li>
        ))}
      </ol>
      {s && f && (
        <Card as="section" padding="20px" aria-labelledby="setup-step">
          <h2 id="setup-step" className="el-h2" style={{ marginBottom: 4 }}>{cur.title}</h2>
          <p className="el-hint" style={{ marginTop: 0 }}>{cur.intro}</p>
          <form onSubmit={(e) => { e.preventDefault(); void next(); }}>
            {cur.key === 'business' && <BusinessFields s={s} f={f} set={set} idp="setup" />}
            {cur.key === 'vat' && <VatFields s={s} f={f} set={set} idp="setup" />}
            {cur.key === 'year' && <YearFields s={s} f={f} set={set} idp="setup" />}
            {cur.key === 'done' && <Summary s={s} />}
            {error && <div className="el-error-box" role="alert" style={{ marginTop: 16 }}>{error}</div>}
            <div className="el-row" style={{ gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
              {step > 0 && <Button disabled={busy} onClick={() => { setError(null); setStep(step - 1); }}>Back</Button>}
              <Button type="submit" variant="primary" disabled={busy || (cur.key === 'business' && !f.legal_name.trim())}>
                {busy ? 'Saving…' : cur.key === 'done' ? 'Start using the books' : 'Save and continue'}
              </Button>
              <span style={{ flex: 1 }} />
              {cur.key !== 'done' && <Button variant="ghost" disabled={busy} onClick={later}>Finish later</Button>}
            </div>
          </form>
        </Card>
      )}
    </>
  );
}

function Summary({ s }: { s: OrganisationSettings }) {
  const fy = Number(String(s.first_year ?? '').replace(/\D/g, ''));
  const r = fy ? firstYearRange(s.year_start_month, fy) : null;
  const rows: [string, string][] = [
    ['Business', [s.legal_name, s.trading_name && `trading as ${s.trading_name}`].filter(Boolean).join(', ')],
    ['Country', s.jurisdiction === 'UK' ? 'United Kingdom (HMRC)' : 'Ireland (Revenue)'],
    ['VAT', s.vat_registered
      ? `${s.vat_number ?? 'Number not entered'} · ${FREQUENCIES.find((x) => x.value === s.vat_frequency)?.label.toLowerCase()} (${periodCycle(s.vat_frequency, s.vat_period_start_month)}) · ${s.vat_basis === 'cash' ? 'cash basis' : 'invoice basis'}`
      : 'Not registered'],
    ['Financial year', r ? `FY${fy}: ${longDate(r.from)} to ${longDate(r.to)}` : '—'],
    ['Currency', s.base_currency],
  ];
  return (
    <dl className="el-kv">
      {rows.map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v}</dd></div>))}
    </dl>
  );
}
